import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_API_URL } from '../../../../utils/constants';
import { myFirebaseAuth } from '../../../../firebase/config';
import { CollectionBuyerEngagement, CollectionBuyerEngagementType } from '@dill/dill-shared';

export type EngagementClaimant = { type: CollectionBuyerEngagementType; id: string };

// A single fetch-the-whole-map hook for the CollectionTrigger / CollectionSequence global
// mutual-exclusion rule: a buyer may be attached to at most one trigger's target list OR one
// active/paused sequence enrollment at a time. Kept as a plain hook (not a redux slice) since it's
// only consumed by a handful of picker components here and on the customer page.
export const useCollectionBuyerEngagements = (supplierId: string) => {
  const [engagementsByBuyerId, setEngagementsByBuyerId] = useState<
    Record<string, CollectionBuyerEngagement>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supplierId) return;
    setIsLoading(true);
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) return;
      const response = await axios.get(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionBuyerEngagements/getSupplierCollectionBuyerEngagements`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      const data: CollectionBuyerEngagement[] = response.data?.success
        ? response.data.data ?? []
        : [];
      setEngagementsByBuyerId(Object.fromEntries(data.map((e) => [e.buyerId, e])));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Is this buyer free to be claimed by `ignoreClaimant`? A buyer already claimed by
  // `ignoreClaimant` itself (e.g. re-saving a trigger's own existing target list) doesn't count as
  // a conflict.
  const isBuyerAvailable = useCallback(
    (buyerId: string, ignoreClaimant?: EngagementClaimant): boolean => {
      const existing = engagementsByBuyerId[buyerId];
      if (!existing) return true;
      if (!ignoreClaimant || existing.type !== ignoreClaimant.type) return false;
      const existingId =
        existing.type === 'TRIGGER_TARGET' ? existing.triggerId : existing.sequenceId;
      return existingId === ignoreClaimant.id;
    },
    [engagementsByBuyerId]
  );

  return { engagementsByBuyerId, isLoading, refresh, isBuyerAvailable };
};

export default useCollectionBuyerEngagements;
